import React, { useMemo } from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend } from "recharts";
import { MdOutlineWarning, MdNotificationsActive } from "react-icons/md";

const COLORS = [
  "#dc2626", "#3b82f6", "#10b981", "#f59e0b", "#8b5cf6",
  "#ec4899", "#06b6d4", "#84cc16", "#ef4444", "#6366f1",
  "#14b8a6", "#d946ef", "#f97316", "#22c55e", "#eab308"
];

const AlertsCharts = ({ reportData, loading }) => {
  const parseAlertsByType = (alertsByTypeStr) => {
    if (!alertsByTypeStr) return [];
    try {
      const parsed = JSON.parse(alertsByTypeStr);
      return parsed.map(item => ({
        name: item.alertName,
        value: item.count,
      }));
    } catch {
      return [];
    }
  };

  const parseAlertsByVM = (alertsByVMStr) => {
    if (!alertsByVMStr) return [];
    try {
      const parsed = JSON.parse(alertsByVMStr);
      return parsed;
    } catch {
      return [];
    }
  };

  const getUniqueAlertTypes = (alertsByVM) => {
    const alertTypesSet = new Set();
    alertsByVM.forEach(vm => {
      vm.alertsByType?.forEach(alert => {
        alertTypesSet.add(alert.alertName);
      });
    });
    return Array.from(alertTypesSet);
  };

  const getAlertTypeColor = (alertName, index) => {
    const alertTypes = allAlertTypes;
    const colorIndex = alertTypes.findIndex(type => type === alertName);
    return COLORS[colorIndex % COLORS.length];
  };

  const buildStackedBarData = (alertsByVM, alertTypes) => {
    return alertsByVM.map(vm => {
      const dataPoint = { name: vm.vmName, total: vm.totalAlerts };
      alertTypes.forEach(alertType => {
        const alert = vm.alertsByType?.find(a => a.alertName === alertType);
        dataPoint[alertType] = alert?.count || 0;
      });
      return dataPoint;
    });
  };

  const alertsByTypeData = parseAlertsByType(reportData?.alertsByType);
  const alertsByVMData = parseAlertsByVM(reportData?.alertsByVM);
  const allAlertTypes = getUniqueAlertTypes(alertsByVMData);
  const stackedBarData = buildStackedBarData(alertsByVMData, allAlertTypes);

  const PieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const total = alertsByTypeData.reduce((sum, item) => sum + item.value, 0);
      const percentage = ((payload[0].value / total) * 100).toFixed(1);
      return (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-2 sm:p-3 shadow-lg">
          <p className="text-gray-300 text-xs sm:text-sm mb-1">{payload[0].name}</p>
          <p className="text-primary-cyan text-base sm:text-lg font-bold">{payload[0].value} alerts</p>
          <p className="text-gray-400 text-xs">{percentage}% of total</p>
        </div>
      );
    }
    return null;
  };

  const BarTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const total = payload.reduce((sum, entry) => sum + entry.value, 0);
      return (
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-2 sm:p-3 shadow-lg max-w-[250px] sm:max-w-none">
          <p className="text-white text-xs sm:text-sm font-bold mb-2 break-words">{label}</p>
          <p className="text-primary-cyan text-base sm:text-lg font-bold mb-2">Total: {total} alerts</p>
          <div className="space-y-1">
            {payload.map((entry, idx) => (
              entry.value > 0 && (
                <div key={idx} className="flex items-center gap-2">
                  <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full" style={{ backgroundColor: entry.color }} />
                  <span className="text-gray-300 text-[10px] sm:text-xs break-words">{entry.name}:</span>
                  <span className="text-white text-[10px] sm:text-xs font-semibold">{entry.value}</span>
                </div>
              )
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mt-4 sm:mt-6">
        <div className="bg-primary-dark rounded-xl p-4 sm:p-6 border border-gray-700/50 animate-pulse">
          <div className="h-64 sm:h-80"></div>
        </div>
        <div className="bg-primary-dark rounded-xl p-4 sm:p-6 border border-gray-700/50 animate-pulse">
          <div className="h-64 sm:h-80"></div>
        </div>
      </div>
    );
  }

  const hasAlertsData = alertsByTypeData.length > 0 || alertsByVMData.length > 0;

  if (!hasAlertsData) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mt-4 sm:mt-6">
        <div className="bg-primary-dark rounded-xl p-4 sm:p-6">
          <div className="flex items-center gap-2 mb-4 sm:mb-6 pb-3 border-b border-gray-700">
            <MdOutlineWarning className="text-primary-cyan text-xl sm:text-2xl" />
            <h3 className="text-lg sm:text-xl font-semibold text-white">Alerts by Type</h3>
          </div>
          <div className="text-center py-8 sm:py-12">
            <p className="text-gray-400 text-sm sm:text-base">No alert data available</p>
          </div>
        </div>
        <div className="bg-primary-dark rounded-xl p-4 sm:p-6">
          <div className="flex items-center gap-2 mb-4 sm:mb-6 pb-3 border-b border-gray-700">
            <MdNotificationsActive className="text-primary-cyan text-xl sm:text-2xl" />
            <h3 className="text-lg sm:text-xl font-semibold text-white">Alerts by VM</h3>
          </div>
          <div className="text-center py-8 sm:py-12">
            <p className="text-gray-400 text-sm sm:text-base">No alert data available</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 mt-4 sm:mt-6">
      <div className="bg-primary-dark rounded-xl p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-4 sm:mb-6 pb-3 border-b border-gray-700">
          <MdOutlineWarning className="text-primary-cyan text-xl sm:text-2xl" />
          <h3 className="text-lg sm:text-xl font-semibold text-white">Alerts by Type</h3>
        </div>
        {alertsByTypeData.length === 0 ? (
          <div className="text-center py-6 sm:py-8">
            <p className="text-gray-400 text-sm sm:text-base">No alert type data available</p>
          </div>
        ) : (
          <>
            <div className="h-64 sm:h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={alertsByTypeData}
                    cx="50%"
                    cy="50%"
                    innerRadius={40}
                    outerRadius={70}
                    paddingAngle={2}
                    dataKey="value"
                    animationDuration={1000}
                    label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                    labelLine={{ stroke: "#9CA3AF", strokeWidth: 1 }}
                  >
                    {alertsByTypeData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip content={<PieTooltip />} cursor={false}/>
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-4 sm:mt-6 flex flex-wrap justify-center gap-2 sm:gap-3 max-h-28 sm:max-h-32 overflow-y-auto">
              {alertsByTypeData.map((item, idx) => {
                const total = alertsByTypeData.reduce((sum, i) => sum + i.value, 0);
                const percentage = ((item.value / total) * 100).toFixed(1);
                return (
                  <div key={idx} className="flex items-center gap-1 sm:gap-2">
                    <div
                      className="w-2 h-2 sm:w-3 sm:h-3 rounded-full"
                      style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                    />
                    <span className="text-gray-300 text-[10px] sm:text-xs truncate max-w-[100px] sm:max-w-[150px]">
                      {item.name.length > 20 ? item.name.substring(0, 18) + "..." : item.name}
                    </span>
                    <span className="text-gray-500 text-[10px] sm:text-xs">
                      ({item.value} - {percentage}%)
                    </span>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      <div className="bg-primary-dark rounded-xl p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-4 sm:mb-6 pb-3 border-b border-gray-700">
          <MdNotificationsActive className="text-primary-cyan text-xl sm:text-2xl" />
          <h3 className="text-lg sm:text-xl font-semibold text-white">Alerts by VM</h3>
        </div>
        {stackedBarData.length === 0 ? (
          <div className="text-center py-8 sm:py-12">
            <p className="text-gray-400 text-sm sm:text-base">No VM alert data available</p>
          </div>
        ) : (
          <div className="w-full overflow-x-auto">
            <div className="min-w-[500px]" style={{ height: 380 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={stackedBarData}
                  margin={{ top: 20, right: 10, left: 10, bottom: 60 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" vertical={false} />
                  <XAxis
                    dataKey="name"
                    stroke="#9CA3AF"
                    tick={{ fill: "#9CA3AF", fontSize: 10 }}
                    angle={-25}
                    textAnchor="end"
                    height={70}
                    interval={0}
                  />
                  <YAxis
                    stroke="#9CA3AF"
                    tick={{ fill: "#9CA3AF", fontSize: 10 }}
                    label={{ value: "Number of Alerts", angle: -90, position: "insideLeft", fill: "#9CA3AF", fontSize: 10 }}
                  />
                  <Tooltip content={<BarTooltip />} cursor={false}/>
                  <Legend
                    wrapperStyle={{ fontSize: "10px", paddingTop: "15px" }}
                    iconType="circle"
                    formatter={(value) => <span className="text-gray-300 text-[10px] sm:text-xs">{value}</span>}
                  />
                  {allAlertTypes.map((alertType, idx) => (
                    <Bar
                      key={alertType}
                      dataKey={alertType}
                      stackId="alerts"
                      fill={getAlertTypeColor(alertType, idx)}
                      animationDuration={1000}
                      radius={idx === allAlertTypes.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                    />
                  ))}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AlertsCharts;